import dayjs from 'dayjs';
import type { DashboardHeatmapCell, DashboardRecentOrder } from '@/types/dashboard';
import {
  HEATMAP_BLOCK_HOURS,
  HEATMAP_DAY_LABELS,
} from '@/pages/dashboard/constants/heatmap.constants';

export interface HeatmapBlockCell {
  count: number;
  revenue: number;
}

export interface HeatmapGridResult {
  grid: Map<string, HeatmapBlockCell>;
  maxCount: number;
  peakLabel: string | null;
  displayTotal: number;
}

export function toHeatmapDayIndex(date: dayjs.Dayjs): number {
  const day = date.day();
  return day === 0 ? 6 : day - 1;
}

export function buildHeatmapFromOrders(orders: DashboardRecentOrder[]): DashboardHeatmapCell[] {
  const cells = new Map<string, DashboardHeatmapCell>();

  for (const order of orders) {
    const placedAt = dayjs(order.placedAt);
    if (!placedAt.isValid()) continue;

    const day = toHeatmapDayIndex(placedAt);
    const hour = placedAt.hour();
    const key = `${day}-${hour}`;
    const existing = cells.get(key);

    if (existing) {
      existing.count += 1;
      existing.revenue += order.grandTotal;
      continue;
    }

    cells.set(key, { day, hour, count: 1, revenue: order.grandTotal });
  }

  return Array.from(cells.values());
}

export function mergeHeatmapCells(
  primary: DashboardHeatmapCell[],
  fallback: DashboardHeatmapCell[],
): DashboardHeatmapCell[] {
  if (primary.length > 0) return primary;
  return fallback;
}

export function getHeatmapPeak(cells: DashboardHeatmapCell[]): string | null {
  if (cells.length === 0) return null;

  const peak = cells.reduce((best, cell) => (cell.count > best.count ? cell : best), cells[0]);
  const day = HEATMAP_DAY_LABELS[peak.day] ?? '—';
  const hour = peak.hour;
  const label =
    hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`;

  return `${day} at ${label}`;
}

export function sumHeatmapOrders(cells: DashboardHeatmapCell[]): number {
  return cells.reduce((sum, cell) => sum + cell.count, 0);
}

export function buildHeatmapGrid(
  data: DashboardHeatmapCell[],
  totalOrders?: number,
): HeatmapGridResult {
  const blocks = new Map<string, HeatmapBlockCell>();
  let peak = 0;

  for (const cell of data) {
    const block = Math.floor(cell.hour / HEATMAP_BLOCK_HOURS);
    const key = `${cell.day}-${block}`;
    const existing = blocks.get(key) ?? { count: 0, revenue: 0 };
    existing.count += cell.count;
    existing.revenue += cell.revenue;
    blocks.set(key, existing);
    peak = Math.max(peak, existing.count);
  }

  const summed = sumHeatmapOrders(data);

  return {
    grid: blocks,
    maxCount: peak,
    peakLabel: getHeatmapPeak(data),
    displayTotal: summed > 0 ? summed : (totalOrders ?? 0),
  };
}
